import { UIOrder, UILeg } from "@flights/types";

export interface AirlineReferenceGroup {
  airlineReference: string;
  legs: Array<{ leg: UILeg; legIndex: number }>;
  journeyLeg: UILeg;
  representativeLegIndex: number;
  startLegIndex: number;
}

/**
 * This function groups flight legs by their airline reference (booking reference).
 * It is used to combine legs that share the same airline booking but are part of a multi-leg journey.
 */

export const groupLegsByAirlineReference = (
  order: UIOrder,
  segmentIndex: number,
  legs: UILeg[]
): AirlineReferenceGroup[] => {
  // Early validation - no data means no groups
  if (!order.airOrder.airlineReferencesByLeg?.length || !legs.length) {
    return [];
  }

  const groups: AirlineReferenceGroup[] = [];
  const referenceToLegIndices = new Map<string, number[]>();

  // Build mapping of airline references to their leg indices
  order.airOrder.airlineReferencesByLeg.forEach((airlineRef) => {
    const legIndicesForReference: number[] = [];

    airlineRef.legIdentifier.forEach((identifier) => {
      if (identifier.segmentIndex === segmentIndex) {
        legIndicesForReference.push(identifier.legIndex);
      }
    });

    if (legIndicesForReference.length > 0) {
      referenceToLegIndices.set(airlineRef.reference, legIndicesForReference);
    }
  });

  // Process each airline reference group
  referenceToLegIndices.forEach((legIndices, reference) => {
    const groupLegs = legIndices
      .map((legIndex) => ({
        leg: legs[legIndex],
        legIndex
      }))
      .filter((item) => item.leg);

    if (groupLegs.length > 0) {
      // Ensure legs are in chronological order
      groupLegs.sort((a, b) => a.legIndex - b.legIndex);

      const firstLeg = groupLegs[0].leg;
      const lastLeg = groupLegs[groupLegs.length - 1].leg;

      // Create synthetic leg for display purposes
      // Takes departure from first leg and arrival from last leg
      const journeyLeg: UILeg = {
        ...firstLeg,
        arrivalAirport: lastLeg.arrivalAirport,
        arrivalTime: lastLeg.arrivalTime,
        arrivalTimeTz: lastLeg.arrivalTimeTz
      };

      groups.push({
        airlineReference: reference,
        legs: groupLegs,
        journeyLeg,
        representativeLegIndex: groupLegs[0].legIndex,
        startLegIndex: groupLegs[0].legIndex
      });
    }
  });

  // Return groups in chronological flight order
  return groups.sort((a, b) => a.startLegIndex - b.startLegIndex);
};
